/**
 * Org structure — the line of command, as a diagram.
 *
 * The chart behind the popup shows where a unit sits in the whole force; this
 * shows the one thing that chart cannot, at the size a popup has: the flow
 * straight down the command — every level above the unit, the unit itself, and
 * everything reporting to it — with no panning and no zoom.
 *
 * It draws on the same lineage styles the Compare view uses, so a line of
 * command looks the same wherever this screen shows one.
 */
import Icon from './org-icons';
import { avatarStyle, initials, headColour, headLabel, nf, peopleLabel, type OrgModel, type OrgNode } from './org-model';
import styles from './org-structure.module.css';

interface FlowProps {
  model: OrgModel;
  unit: OrgNode;
  /** Following a step in the flow moves the whole screen to that unit. */
  onSelect: (id: string) => void;
}

const OrgFlow = ({ model, unit: u, onSelect }: FlowProps) => (
  <div className={styles.lin}>
    {u.path.length === 0 && <p className={styles.flowtop}>Top of the tree — nothing sits above it.</p>}

    {u.path.map((id) => {
      const a = model.byId[id];
      return (
        <div key={id} className={styles.flowstep}>
          <button
            type="button"
            className={styles.anc}
            title={`Go to ${a.name} · ${a.typeLabel}`}
            onClick={() => onSelect(id)}
          >
            <span className={styles.sq} style={{ background: headColour(a) }} />
            <span className={styles.nm}>{a.name}</span>
          </button>
          <span className={styles.drop} />
        </div>
      );
    })}

    {/* The unit the popup is about. Marked, because everything else in here is
        a step you can take away from it. */}
    <div className={`${styles.selfcard} ${styles.flowself}`}>
      <div className={styles.band} style={{ background: headColour(u) }} />
      <div className={styles.selfin}>
        <p className={styles.nm}>{u.name}</p>
        {/* Sentence case, to match the title directly above it. This line and
            the popup's header state the same two facts, and rendering the same
            fact two different ways in one panel reads as two facts. */}
        <p className={styles.cd}>
          {u.code} · {u.typeLabel}
        </p>
        {/* Same rule as the rows below it: an officer, or a head post that
            stands empty. Nothing where the unit records neither. */}
        {(u.head || u.headState === 'vacant') && (
          <div className={styles.who}>
            <span className={styles.av} style={avatarStyle(u)} aria-hidden="true">
              {u.head ? initials(u.head.name) : '—'}
            </span>
            <span className={`${styles.n}${u.headState === 'vacant' ? ` ${styles.vacname}` : ''}`}>
              {headLabel(u)}
            </span>
          </div>
        )}
      </div>
    </div>

    {u.kids.length > 0 && <span className={styles.drop} />}

    {u.kids.length ? (
      <div className={styles.flowkids}>
        <p className={styles.kidhd}>
          Sub-Units <span>{u.kids.length}</span>
        </p>
        <div className={styles.kidlist}>
          {u.kids.map((k) => {
            const c = model.byId[k];
            return (
              <button
                key={k}
                type="button"
                className={styles.kidrow}
                title={`Go to ${c.name} · ${c.typeLabel}`}
                onClick={() => onSelect(k)}
              >
                <span className={styles.sq} style={{ background: headColour(c) }} />
                <span className={styles.nm}>
                  {c.name}
                  {/* The officer, or a real vacancy. A unit with no head post on
                      file gets no second line rather than "officer not
                      recorded" repeated down every row of the list. */}
                  {c.head ? (
                    <i>{c.head.name}</i>
                  ) : c.headState === 'vacant' ? (
                    <i className={styles.vacname}>{headLabel(c).toLowerCase()}</i>
                  ) : null}
                </span>
                {/* Printed where somebody is actually posted there. A column of
                    zeroes beside every station measures nothing. */}
                {model.hasHeadcount && c.people > 0 && (
                  <span className={styles.n}>{peopleLabel(model, c)}</span>
                )}
                {c.kids.length ? (
                  <span className={styles.more} title={`${nf(c.subUnits)} below`}>
                    +{nf(c.kids.length)}
                  </span>
                ) : (
                  <Icon name="chev" className={`${styles.ic} ${styles.go}`} size={12} />
                )}
              </button>
            );
          })}
        </div>
      </div>
    ) : (
      <p className={styles.nokids}>Last level — nothing reports to it.</p>
    )}
  </div>
);

export default OrgFlow;
